import { apiV2 } from '../lib/baseAxios';
import { IVFSResponse } from './fileSystemService';

/**
 * Cancellation flow services
 * These services handle the native in-app cancellation flow,
 * replacing the Churnkey integration.
 */

export interface PackageFeatures {
  recordingRequestsCount: number; // -1 for unlimited
  transcriptionCount: number; // -1 for unlimited
  isPaid: boolean; // Whether user has a paid plan (vs free)
  term: 'monthly' | 'annual'; // Subscription term for proper i18n
}

export interface RetentionOfferInfo {
  type: 'discount_50' | 'trial_extension';
  extensionDays?: number; // For trial extensions
  discountPercentage?: number; // For discount offers
  durationMonths?: number; // For monthly discount offers (e.g., 3)
  durationType?: 'repeating' | 'once'; // How discount is applied
  originalPrice?: number;
  discountedPrice?: number;
  totalSavings?: number;
  term?: 'monthly' | 'annual';
}

export interface CancellationInfo {
  subscriptionType: 'trial' | 'paid';
  packageFeatures: PackageFeatures;
  eligibleForRetention: boolean;
  retentionOffer?: RetentionOfferInfo;
  currentPeriodEnd: string;
  creditsRemaining?: number;
  isAlreadyCanceled: boolean;
  cancelAt?: string;
}

export interface SurveyResponse {
  reason?: string;
  feedback?: string;
}

export interface ScheduleCancelResponse {
  success: boolean;
  cancelAt: string;
  message: string;
}

export interface AcceptRetentionOfferResponse {
  success: boolean;
  message: string;
  newPeriodEnd?: string;
}

export interface ReactivateSubscriptionResponse {
  success: boolean;
  message: string;
}

export interface SurveyConfig {
  reasons: Array<{
    id: string;
    label: string;
    hint?: string;
  }>;
  feedbackPlaceholder: string;
}

/**
 * Get cancellation information for the team
 * Returns subscription type, features, and eligibility for retention offers
 */
export const getCancellationInfo = async ({
  teamId,
}: {
  teamId: string;
}): Promise<CancellationInfo> => {
  const response = await apiV2.get<IVFSResponse<CancellationInfo>>(
    `/billing/subscriptions/${teamId}/cancellation-info`,
  );
  return response.data.data;
};

/**
 * Schedule subscription cancellation at period end
 * Optionally submits survey response for paid subscriptions
 */
export const scheduleCancellation = async ({
  teamId,
  surveyResponse,
}: {
  teamId: string;
  surveyResponse?: SurveyResponse;
}): Promise<ScheduleCancelResponse> => {
  const response = await apiV2.post<IVFSResponse<ScheduleCancelResponse>>(
    `/billing/subscriptions/${teamId}/schedule-cancel`,
    { surveyResponse },
  );
  return response.data.data;
};

/**
 * Accept a retention offer (50% discount or trial extension)
 */
export const acceptRetentionOffer = async ({
  teamId,
  offerType,
}: {
  teamId: string;
  offerType: 'discount_50' | 'trial_extension';
}): Promise<AcceptRetentionOfferResponse> => {
  const response = await apiV2.post<IVFSResponse<AcceptRetentionOfferResponse>>(
    `/billing/subscriptions/${teamId}/accept-retention-offer`,
    { offerType },
  );
  return response.data.data;
};

/**
 * Get Stripe survey configuration
 * Returns the survey questions and options
 */
export const getStripeSurveyConfig = async ({
  teamId,
}: {
  teamId: string;
}): Promise<SurveyConfig> => {
  const response = await apiV2.get<IVFSResponse<SurveyConfig>>(
    `/billing/subscriptions/${teamId}/stripe-survey-config`,
  );
  return response.data.data;
};

/**
 * Reactivate a subscription that was scheduled for cancellation
 * Removes the cancellation schedule and continues the subscription
 */
export const reactivateSubscription = async ({
  teamId,
}: {
  teamId: string;
}): Promise<ReactivateSubscriptionResponse> => {
  const response = await apiV2.post<
    IVFSResponse<ReactivateSubscriptionResponse>
  >(`/billing/subscriptions/${teamId}/reactivate`);
  return response.data.data;
};
